import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";

import { Observable, catchError, of, tap } from "rxjs";

import { environment } from "@environments/environment";

import { AuthResponse } from "./models";
import { LocalStorageService, TenantService, LoggingService } from "@core/services";
import { HANDLE_ERROR } from "@core/interceptors";


const TENANT_ID_KEY: string = 'tenantId';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private _warningTimerID: any = null;

  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _router: Router = inject(Router);
  private readonly _localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly _tenantService: TenantService = inject(TenantService);
  private readonly _loggingService: LoggingService = inject(LoggingService);

  authResponse = signal<AuthResponse | undefined>(undefined);

  constructor() { }

  authenticate(tenantId: string, username: string, password: string): Observable<AuthResponse> {
    this._loggingService.debug('[tokenService] authenticate');

    const authRequest = { username: username, password: password }
    
    // withCredentials: true ensures HTTP-only cookies (including refresh token cookie) are received
    const requestOptions = { withCredentials: true };

    return this._http.post<AuthResponse>(`${environment.authUrl}/v1/${tenantId}/token/authenticate`, authRequest, requestOptions).pipe(
      tap((authResponse) => {
        this.authResponse.set(authResponse);
        this._localStorageService.set<string>(TENANT_ID_KEY, authResponse.tenantId);
        this.startRefreshTokenTimer(authResponse);
      }));
  }

  refreshToken(tenantId: string | undefined = undefined): Observable<AuthResponse | undefined> {
    this._loggingService.debug('[tokenService] refreshToken');
    
    const routeTenant = this._tenantService.currentUriTenant();

    const localStorageTenant = this._localStorageService.get<string>(TENANT_ID_KEY);
    if (localStorageTenant !== routeTenant) {
      this._localStorageService.remove(TENANT_ID_KEY);
    }

    const lastTenantId: string | undefined = tenantId ?? routeTenant;
    if (!lastTenantId) {
      this._loggingService.debug('[tokenService] no tenant ID available');
      return of(undefined);
    }
    
    // withCredentials: true ensures HTTP-only cookies (including refresh token cookie) are sent
    const headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
    const context = new HttpContext().set(HANDLE_ERROR, true);
    const requestOptions = { 
      headers: headers, 
      withCredentials: true,  // Critical: sends HTTP-only refresh token cookie
      context: context 
    };

    this._loggingService.debug('[tokenService] attempting to refresh');
    const url = `${environment.authUrl}/v1/${lastTenantId}/token/refresh`;

    return this._http.post<AuthResponse>(url, {}, requestOptions).pipe(
      tap((authResponse) => {
        this._loggingService.debug('[authService] successfully refreshed Token');
        this.authResponse.set(authResponse);
        this.startRefreshTokenTimer(authResponse);
        return authResponse;
      }),
      catchError((err) => {
        this._loggingService.error('[tokenService] failed to refresh token. Error', err.message);

        this.resetCredentials();
        
        const tenantId = this._tenantService.currentUriTenant();
        
        //this.redirectToLogin(tenantId);
        
        return of(undefined);
      }));
  }

  revoke() {
    this._loggingService.debug('[tokenService] revoke');
    if (!this.authResponse()) { return; }

    this._http.post<any>(`${environment.authUrl}/v1/${this.authResponse()!.tenantId}/token/revoke`, {}, { withCredentials: true }).subscribe();

    this.resetCredentials();
  }

  hasClaim(claim: string | undefined) {
    if (!this.authResponse()) { return false; }

    const token = this.authResponse()?.token;
    if (!token || (token?.length ?? 0) === 0) { return false; }

    let decodedJWT = JSON.parse(window.atob(token.split('.')[1]));

    // check validity
    var now = new Date().getTime() / 1000;
    var isTokenExpiryValid = decodedJWT.nbf < now && now < decodedJWT.exp;
    if (!isTokenExpiryValid) { return false; }
    
    // TODO: Actually validate claims on token
    
    return true;
  }

  private redirectToLogin(tenantId: string): void {
    this._loggingService.debug('[tenantService] Redirect to login');
    this._router.navigate([tenantId, 'kiosk', 'login']);
  }

  resetCredentials() {
    this.authResponse.set(undefined);
    this._localStorageService.remove(TENANT_ID_KEY);
    clearTimeout(this._warningTimerID);
  }

  private startRefreshTokenTimer(authResponse: AuthResponse) {
    const jwtToken = JSON.parse(atob(authResponse.token.split('.')[1]));
    const expires = new Date(jwtToken.exp * 1000);

    const timeout = expires.getTime() - Date.now() - (60 * 1000);
    this._warningTimerID = setTimeout(() => this.refreshToken().subscribe(), timeout);
  }
}